import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { RequestService } from '../request/request.service';
import { ProfileService } from '../profile/profile.service';

export interface SocialInteractionNotification {
  recipientProfileId?: number;
  remoteTitle: string;
  remoteBody: string;
  localTitle: string;
  localBody: string;
  extra?: Record<string, any>;
  notifyRemote?: boolean;
  scheduleAt?: Date;
}

@Injectable({ providedIn: 'root' })
export class SocialInteractionNotificationService {
  constructor(private request: RequestService, private profileService: ProfileService) {}

  async notifyPublicationLike(publication: any): Promise<void> {
    let actorUsername = 'Alguien';
    try {
      const response: any = await this.profileService.getProfileInfo();
      const profile = typeof response === 'string' ? JSON.parse(response) : response;
      if (profile?.username) {
        actorUsername = `@${profile.username}`;
      }
    } catch (error) {
      console.error('Could not load the reacting profile for notification:', error);
    }

    return this.notifySuccessfulInteraction({
      recipientProfileId: Number(publication?.userProfile?.user_id || 0),
      notifyRemote: !publication?.owner,
      remoteTitle: 'Nuevo me gusta en tu publicación',
      remoteBody: `${actorUsername} indicó que le gusta tu publicación.`,
      localTitle: 'Me gusta enviado',
      localBody: `Tu reacción a la publicación de @${publication?.userProfile?.username || 'Laniakea'} se guardó correctamente.`,
      extra: { publication_id: publication?.id, interaction_type: 'publication_like' },
    });
  }

  async notifySuccessfulInteraction(options: SocialInteractionNotification): Promise<void> {
    const tasks: Promise<any>[] = [];
    const recipientProfileId = Number(options.recipientProfileId || 0);

    if (options.notifyRemote !== false && recipientProfileId > 0) {
      tasks.push(
        this.request.createRequestPost('GreatAtractorNotifications', {
          title: options.remoteTitle,
          body: options.remoteBody,
          profile_id: recipientProfileId,
          user_id: recipientProfileId,
          delivery_mode: 'push_only',
          push_only: true,
          ...options.extra,
        }).then((response: any) => {
          const parsed = typeof response === 'string' ? JSON.parse(response) : response;
          if (parsed?.success === false) {
            throw new Error(parsed?.error || 'Firebase did not accept the social notification');
          }
          return parsed;
        }).catch((error) => {
          console.error('Social interaction push notification failed:', error);
        }),
      );
    }

    tasks.push(this.scheduleLocalConfirmation(options));
    await Promise.all(tasks);
  }

  async notifyLocal(title: string, body: string, extra: Record<string, any> = {}): Promise<void> {
    await this.scheduleLocalConfirmation({
      remoteTitle: '',
      remoteBody: '',
      localTitle: title,
      localBody: body,
      extra,
      notifyRemote: false,
    });
  }

  async scheduleLocal(title: string, body: string, at: Date, extra: Record<string, any> = {}): Promise<void> {
    if (!(at instanceof Date) || Number.isNaN(at.getTime()) || at.getTime() <= Date.now()) {
      return;
    }
    await this.scheduleLocalConfirmation({
      remoteTitle: '',
      remoteBody: '',
      localTitle: title,
      localBody: body,
      extra,
      notifyRemote: false,
      scheduleAt: at,
    });
  }

  private async scheduleLocalConfirmation(options: SocialInteractionNotification): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    try {
      let permission = await LocalNotifications.checkPermissions();
      if (permission.display === 'prompt' || permission.display === 'prompt-with-rationale') {
        permission = await LocalNotifications.requestPermissions();
      }
      if (permission.display !== 'granted') {
        console.warn('Local notifications permission was not granted:', permission.display);
        return;
      }

      await LocalNotifications.schedule({
        notifications: [{
          id: Math.floor(Date.now() % 2147483647),
          title: options.localTitle,
          body: options.localBody,
          schedule: { at: options.scheduleAt || new Date(Date.now() + 250) },
          sound: null,
          attachments: null,
          actionTypeId: '',
          extra: options.extra || {},
          channelId: null,
        }],
      });
    } catch (error) {
      console.error('Social interaction local notification failed:', error);
    }
  }
}
